import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Icon } from "@dtw/icons";
import {
  authMode,
  login,
  setMockSession,
  sessionQueryKey,
  useSession,
  MOCK_ROLES,
} from "@/lib/auth";
import "./login.css";

interface LoginSearch {
  redirect?: string;
  error?: string;
}

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search["redirect"] === "string" ? (search["redirect"] as string) : undefined,
    error: typeof search["error"] === "string" ? (search["error"] as string) : undefined,
  }),
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const mode = authMode();

  // Already authenticated → leave the gateway.
  useEffect(() => {
    if (session) void navigate({ to: "/overzicht" });
  }, [session, navigate]);

  return (
    <div className="login" data-screen-label="Gateway Login">
      <aside className="login-brand">
        <div className="login-brand__lockup">
          <img src="/logo-white.png" alt="Datow Coöperatie U.A." />
        </div>
        <div className="login-brand__payoff">
          <div className="big">
            Your data <em>in motion</em>.
          </div>
          <p className="sub">
            Datow maakt het werken met data uit verschillende systemen eenvoudig en betaalbaar —
            data, integratie en processen voor transport en logistiek.
          </p>
        </div>
        <div className="login-brand__foot">
          <span>
            <Icon name="map-pin" size={12} /> Waalwijk
          </span>
          <span>
            <Icon name="database" size={12} /> Data &amp; integratie
          </span>
          <span>
            <Icon name="leaf" size={12} /> CO₂-analyse · ISO 14083
          </span>
        </div>
      </aside>

      <main className="login-form">
        <div className="login-card">
          <h1>{t("login.title")}</h1>
          <p className="lede">{t("login.lede")}</p>

          {mode === "mock" ? (
            <MockLoginForm
              onLogin={async (email, roles) => {
                setMockSession({ email, roles });
                await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
                void navigate({ to: "/overzicht" });
              }}
            />
          ) : (
            <div className="login-section">
              <div className="login-section__label">
                <Icon name="user" size={13} /> {t("login.employees")}
              </div>
              <button className="dtw-btn dtw-btn--primary dtw-btn--lg" onClick={() => login()}>
                <Icon name="microsoft" size={15} />
                {t("login.microsoft")}
              </button>
              <p className="sso-note">{t("login.ssoNote")}</p>
            </div>
          )}

          <div className="partner" aria-disabled="true">
            <div className="partner__head">
              <span className="partner__title">
                <Icon name="link" size={13} /> {t("login.partner")}
              </span>
              <span className="dtw-chip dtw-chip--cyan">{t("login.partnerBadge")}</span>
            </div>
            <p className="partner__body">{t("login.partnerBody")}</p>
            <button className="dtw-btn dtw-btn--secondary dtw-btn--lg" type="button" disabled>
              {t("login.partnerButton")}
            </button>
          </div>

          <div className="login-help">
            <span>{t("login.help")}</span>
            <span className="t-mono">{t("login.contactEmail")}</span>
          </div>
        </div>
      </main>
    </div>
  );
}

function MockLoginForm({ onLogin }: { onLogin: (email: string, roles: string[]) => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("mila@datow.nl");
  const [roles, setRoles] = useState<string[]>(["admin"]);

  function toggleRole(role: string) {
    setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  }

  return (
    <div className="login-dev">
      <div className="login-section__label">
        <Icon name="key" size={13} /> {t("login.devTitle")}
      </div>
      <div className="dtw-field">
        <label className="dtw-field__label">{t("login.devEmail")}</label>
        <input
          className="dtw-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="off"
        />
      </div>
      <div className="dtw-field__label" style={{ marginTop: "var(--space-2)" }}>
        {t("login.devRoles")}
      </div>
      <div className="login-dev__roles">
        {MOCK_ROLES.map((role) => (
          <label className="login-dev__role" key={role}>
            <input type="checkbox" checked={roles.includes(role)} onChange={() => toggleRole(role)} />
            {role}
          </label>
        ))}
      </div>
      <button
        className="dtw-btn dtw-btn--primary dtw-btn--lg"
        disabled={roles.length === 0 || !email}
        onClick={() => onLogin(email, roles)}
      >
        {t("login.devLogin")}
      </button>
    </div>
  );
}
